import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import ThemeToggle from '../components/ui/ThemeToggle'

const RegisterPage = () => {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validações básicas
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem')
      setLoading(false)
      return
    }

    if (formData.password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres')
      setLoading(false)
      return
    }

    if (!formData.acceptTerms) {
      setError('Você deve aceitar os termos de uso')
      setLoading(false)
      return
    }

    try {
      // Registrar e fazer login automaticamente
      const { register } = await import('../services/authService')
      await register.default.register({
        name: formData.name,
        email: formData.email,
        password: formData.password
      })
      
      // Fazer login após registro
      await login({
        email: formData.email,
        password: formData.password
      })
      // Redirecionar para o dashboard após registro bem-sucedido
      navigate('/app')
    } catch (error) {
      setError(error.message || 'Erro ao criar conta. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center text-green-600 dark:text-dark-accent-green hover:text-green-700 dark:hover:text-dark-accent-greenLight transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para o site
          </Link>
          <ThemeToggle />
        </div>
        
        <div className="flex items-center justify-center mb-6">
          <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">C</span>
          </div>
          <span className="ml-3 text-2xl font-bold text-gray-900 dark:text-dark-text-primary">Cotação.ai</span>
        </div>
        
        <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-dark-text-primary">
          Crie sua conta gratuita
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-dark-text-secondary">
          Já tem uma conta?{' '}
          <Link to="/login" className="font-medium text-green-600 dark:text-dark-accent-green hover:text-green-500 dark:hover:text-dark-accent-greenLight transition-colors">
            Faça login
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-dark-bg-secondary py-8 px-4 shadow-lg dark:shadow-none sm:rounded-lg sm:px-10 transition-colors border border-gray-200 dark:border-dark-border-primary">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary">
                Nome completo
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-md placeholder-gray-400 dark:placeholder-dark-text-muted bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-green-500 dark:focus:ring-dark-accent-green focus:border-green-500 dark:focus:border-dark-accent-green sm:text-sm transition-colors"
                  placeholder="Seu nome completo"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary">
                Email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-dark-border-primary rounded-md placeholder-gray-400 dark:placeholder-dark-text-muted bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-green-500 dark:focus:ring-dark-accent-green focus:border-green-500 dark:focus:border-dark-accent-green sm:text-sm transition-colors"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary">
                Senha
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-dark-border-primary rounded-md placeholder-gray-400 dark:placeholder-dark-text-muted bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-green-500 dark:focus:ring-dark-accent-green focus:border-green-500 dark:focus:border-dark-accent-green sm:text-sm transition-colors"
                  placeholder="Mínimo 8 caracteres"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 dark:text-dark-text-muted" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 dark:text-dark-text-muted" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary">
                Confirmar senha
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-dark-border-primary rounded-md placeholder-gray-400 dark:placeholder-dark-text-muted bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-green-500 dark:focus:ring-dark-accent-green focus:border-green-500 dark:focus:border-dark-accent-green sm:text-sm transition-colors"
                  placeholder="Confirme sua senha"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 dark:text-dark-text-muted" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 dark:text-dark-text-muted" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="acceptTerms"
                name="acceptTerms"
                type="checkbox"
                checked={formData.acceptTerms}
                onChange={handleChange}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="acceptTerms" className="ml-2 block text-sm text-gray-900 dark:text-dark-text-primary">
                Aceito os{' '}
                <a href="#" className="text-green-600 dark:text-dark-accent-green hover:text-green-500 dark:hover:text-dark-accent-greenLight">
                  termos de uso
                </a>{' '}
                e{' '}
                <a href="#" className="text-green-600 dark:text-dark-accent-green hover:text-green-500 dark:hover:text-dark-accent-greenLight">
                  política de privacidade
                </a>
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                  loading 
                    ? 'bg-green-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {loading ? 'Criando conta...' : 'Criar conta gratuita'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800 dark:text-green-400">
                    Teste gratuito de 30 dias
                  </h3>
                  <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Acesso completo a todas as funcionalidades</li>
                      <li>Cotações ilimitadas</li>
                      <li>Suporte prioritário</li>
                      <li>Sem compromisso, cancele quando quiser</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
